import {
  DetailedHTMLProps,
  Fragment,
  InputHTMLAttributes,
  useEffect,
  useRef,
  useState,
} from "react";

import XMarkIcon from "@heroicons/react/20/solid/XMarkIcon";
import { nanoid } from "nanoid";
import { RegisterOptions, useFormContext, useWatch } from "react-hook-form";

import TextButton from "components/button/textButton/TextButton";
import { useGetProduct } from "queries/legacy/product-queries";
import { CANVAS_FALLBACK_CONTENT } from "utils/fallback-content";
import { useInterpolate } from "utils/prismic-utils";

import SingleLineText from "../singleLineText/SingleLineText";

import styles from "./MultiText.module.scss";

type RefinedInputProps = Omit<
  DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
  "className" | "type"
>;

type MultiTextProps = RefinedInputProps & {
  maxFields: number;
  minFields: number;
  name: string;
  registerOptions: RegisterOptions;
};

/**
 * List of SingleLineText fields, returns an array of strings
 *
 * RHF's `useFieldArray` is normally used for dynamic fields like this,
 * but doesn't support arrays of strings like we want.
 *
 * So instead, we maintain a copy of the fields with a unique ID,
 * keeping it synced with the form values for validation.
 */
export default function MultiText({
  maxFields,
  minFields,
  name,
  registerOptions,
  ...inputProps
}: MultiTextProps) {
  const {
    formState: { errors },
    register,
    setFocus,
    unregister,
  } = useFormContext();

  const values = useWatch({ name });

  const { data: product } = useGetProduct();

  const interpolate = useInterpolate();

  /**
   * A copy of the fields used to track adding and removing.
   *
   * Use `nanoid` to provide a unique key for tracking the array.
   */
  const [fields, setFields] = useState<{ id: string }[]>(
    Array.from({ length: Math.max(values?.length || 0, minFields) }, () => ({
      id: nanoid(5),
    }))
  );

  const prevLengthRef = useRef(fields.length);

  /**
   * Every time the fields change, let RHF know
   */
  useEffect(() => {
    // Focus new fields when added
    if (fields.length > prevLengthRef.current) {
      setFocus(`${name}.${fields.length - 1}`);
    }

    prevLengthRef.current = fields.length;
  }, [setFocus, name, fields]);

  const addField = () => {
    const newFields = [...fields, { id: nanoid(5), value: "" }];
    setFields(newFields);
  };

  const removeField = () => {
    const newFields = [...fields];
    newFields.pop();
    setFields(newFields);
    unregister(`${name}.${newFields.length}`);
  };

  return (
    <div className={styles.fields}>
      {fields.length === 0 && (
        // Must have an input registered to allow zero fields
        <input type="hidden" {...register(name, { value: "" })} />
      )}

      {fields.map(({ id }, i) => {
        const error =
          errors?.[name] &&
          Array.isArray(errors[name]) &&
          (errors[name] as any)[i];

        return (
          <Fragment key={id}>
            <div className={styles.field}>
              <SingleLineText
                aria-invalid={!!error}
                className={styles.input}
                {...inputProps}
                {...register(`${name}.${i}`, registerOptions)}
              />

              {/* Only allow the final field to be removed to avoid index problems */}
              {fields.length > minFields && i === fields.length - 1 && (
                <button
                  aria-label="Remove"
                  className={styles.removeButton}
                  onClick={() => removeField()}
                  type="button"
                >
                  <XMarkIcon />
                </button>
              )}
            </div>

            {error && <p className={styles.error}>{error.message}</p>}
          </Fragment>
        );
      })}

      {fields.length < maxFields && (
        <TextButton
          className={styles.addButton}
          color="gray"
          onClick={() => addField()}
          type="button"
        >
          {interpolate(
            product?.content.canvas.add_another_button_label ??
              CANVAS_FALLBACK_CONTENT.add_another_button_label
          )}
        </TextButton>
      )}
    </div>
  );
}
